import React, { useEffect, useState } from 'react';
import Modal from '../shared/Modal';
import { TextField, SelectField } from '../shared/FormField';
import { buildSpeciesOptions, parseApiError } from '../FoundationalTables/SpeciesLivestockRegistry/registryHelpers';

export default function EditAgeClassModal({ isOpen, onClose, onSave, row, species = [] }) {
  const [form, setForm] = useState({
    species_uuid: '',
    age_class_code: '',
    age_class_label: '',
    age_range_description: '',
    sort_order: '',
  });
  const [status, setStatus] = useState('idle');
  const [errMsg, setErrMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const set = (key) => (value) => setForm((current) => ({ ...current, [key]: value }));

  useEffect(() => {
    if (!row) return;

    setForm({
      species_uuid: row.species_uuid ?? '',
      age_class_code: row.age_class_code ?? '',
      age_class_label: row.age_class_label ?? '',
      age_range_description: row.age_range_description ?? '',
      sort_order: row.sort_order ?? '',
    });
    setStatus('idle');
    setErrMsg('');
    setSubmitting(false);
  }, [row]);

  const speciesOptions = buildSpeciesOptions(species);

  const submit = async () => {
    if (submitting || !row?.uuid) return;

    if (
      !form.species_uuid ||
      !form.age_class_code.trim() ||
      !form.age_class_label.trim() ||
      !form.age_range_description.trim() ||
      form.sort_order === '' ||
      form.sort_order === null ||
      Number.isNaN(Number(form.sort_order))
    ) {
      setErrMsg('Please complete the required fields before saving.');
      setStatus('error');
      return;
    }

    setSubmitting(true);
    setStatus('idle');
    setErrMsg('');

    try {
      await onSave({
        uuid: row.uuid,
        species_uuid: form.species_uuid,
        age_class_code: form.age_class_code.trim(),
        age_class_label: form.age_class_label.trim(),
        age_range_description: form.age_range_description.trim(),
        sort_order: Number(form.sort_order),
      });
      setStatus('success');
    } catch (error) {
      setErrMsg(parseApiError(error, 'Unable to update age class.'));
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setStatus('idle');
    setErrMsg('');
    setSubmitting(false);
    onClose();
  };

  return (
    <Modal
      title="Edit Age Class"
      isOpen={isOpen}
      onClose={handleClose}
      onSubmit={submit}
      status={status}
      errorMessage={errMsg}
      onContinue={handleClose}
      submitLabel="Save Changes"
      successTitle="Age Class Updated"
      successMessage="The age class changes were saved to the backend."
      isSubmitting={submitting}
    >
      <TextField
        label="Age Class Code"
        required
        value={form.age_class_code}
        onChange={set('age_class_code')}
        readOnly
        className="cursor-not-allowed bg-gray-50 font-mono"
        note="System identifier - cannot be edited after creation."
      />
      <SelectField
        label="Species"
        required
        value={form.species_uuid}
        onChange={set('species_uuid')}
        options={speciesOptions}
        placeholder="Select Species"
      />
      <TextField
        label="Age Class Label"
        required
        value={form.age_class_label}
        onChange={set('age_class_label')}
        placeholder="e.g. Calf"
      />
      <TextField
        label="Age Range Description"
        required
        value={form.age_range_description}
        onChange={set('age_range_description')}
        placeholder="e.g. < 6 months"
      />
      <TextField
        label="Sort Order"
        required
        type="number"
        value={form.sort_order}
        onChange={set('sort_order')}
        placeholder="e.g. 1"
      />
    </Modal>
  );
}
